import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject, Observable, combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, map, tap, filter } from 'rxjs/operators';
import { CupcakeDropdownTriggerDirective } from '@ipreo/cupcake-components';
import { ReusableStreamProperty } from '@ipreo/muni-shared';
import { SelectableSeries } from '../../models/series';
import { GameDayRootReducerState } from '../../../state/root.state';
import { FilterBySeries } from '../../../state/data/actions';
import {
  getGamedayDealState,
  getGamedayFiltersState
} from '../../../state/data/reducer';
import { GameDayModel } from '../../../models/gameday-model';

@Component({
  selector: 'muni-gameday-filter-by-series',
  templateUrl: './filter-by-series.template.html',
  styleUrls: ['./filter-by-series.styles.scss']
})
export class FilterBySeriesComponent implements OnDestroy {
  @ViewChild('filterDropdown', { read: CupcakeDropdownTriggerDirective })
  public filterDropdown: CupcakeDropdownTriggerDirective;

  private selectedSeriesId: string[];
  public readonly filter: Subject<SelectableSeries> = new Subject<
    SelectableSeries
  >();

  private readonly deal: Observable<GameDayModel.Deal> = this.store.pipe(
    select(getGamedayDealState),
    filter(
      (deal: GameDayModel.Deal) =>
        !!(deal && deal.series && deal.series.length > 0)
    )
  );

  private readonly seriesIdsFilter: Observable<string[]> = this.store.pipe(
    select(getGamedayFiltersState),
    map(({ seriesId }) => seriesId)
  );

  @ReusableStreamProperty
  private readonly series: Observable<SelectableSeries[]> = combineLatest(
    this.deal,
    this.seriesIdsFilter
  ).pipe(
    map(
      ([{ series }, seriesIds]: [
        GameDayModel.Deal,
        string[]
      ]): SelectableSeries[] =>
        !seriesIds
          ? series.map((s: GameDayModel.Series) => ({
              ...s,
              selected: true
            }))
          : series.map((s: GameDayModel.Series) => ({
              ...s,
              selected: seriesIds.indexOf(s.id) !== -1
            }))
    ),
    distinctUntilChanged()
  );

  public readonly selectableSeries: Observable<
    SelectableSeries[]
  > = this.series.pipe(
    tap(series => {
      this.selectedSeriesId = series
        .filter(({ selected }: SelectableSeries) => selected)
        .map(({ id }: SelectableSeries) => id);
    })
  );

  public readonly selectedSeriesCodes: Observable<string[]> = this.series.pipe(
    map((selectableSeries: SelectableSeries[]) =>
      selectableSeries
        .filter(({ selected }: SelectableSeries) => selected)
        .map(({ code }: SelectableSeries) => code)
    ),
    distinctUntilChanged()
  );

  private readonly filterSubscription: Subscription = this.filter
    .asObservable()
    .subscribe((value: SelectableSeries) => this.filterBySeries(value));

  constructor(private store: Store<GameDayRootReducerState>) {}

  public ngOnDestroy(): void {
    this.filterSubscription.unsubscribe();
  }

  public getSelectedSeriesCode(code: string, last: boolean): string {
    return code + (!last ? ', ' : '');
  }

  public toggleDropdown(): void {
    if (this.filterDropdown.isExpanded()) {
      this.filterDropdown.collapse();
    } else {
      this.filterDropdown.expand();
    }
  }

  private filterBySeries(series: SelectableSeries): void {
    let seriesId: string[];

    series.selected = !series.selected;

    if (!series.selected) {
      seriesId = this.selectedSeriesId.filter(id => id !== series.id);
    } else {
      seriesId = this.selectedSeriesId.slice();
      seriesId.push(series.id);
    }

    this.selectedSeriesId = seriesId;
    this.store.dispatch(new FilterBySeries(seriesId));
  }
}
